/**
 * Client-side access to the per-clinic payer alias map.
 *
 *  - `fetchPayerAliases` reads the subcollection (admin / clinic-member gated by
 *    the `payerAliases` carve-out in firestore.rules) — backs the CSV upload
 *    ranker, which also reads each rule's review state.
 *  - `setPayerAlias` invokes the backend callable (the practice's one write
 *    path; client writes are denied by rules) to create or confirm an alias.
 *  - The `*ForStaff` wrappers below are the staff console's Payer Mappings
 *    board. No client ever deletes a rule directly: deletion is the audited
 *    `deletePayerAliasForStaff` callable, and firestore.rules denies the rest.
 *
 * Mirrors the `setPayerAlias` callable on the server. Reads degrade to an
 * empty list on permission/network failure so they never block a flow (same
 * posture as `fetchPayerUsage`).
 */

import { collection, getDocs } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../config'
import type {
  PayerAlias,
  PayerAliasDecidedBy,
  PayerAliasExpected,
  PayerAliasHealth,
  PayerAliasLearned,
  PayerAliasReview,
  PayerAliasReviewState,
  PayerAliasRoute,
  PayerAliasSource,
} from '../../shared/payerAlias'
import type { EligibilityResultWithMembers } from '../../shared/pipelineTypes'
import type { CorrectionOverride, EligibilityCheckDoc, UserPickOverride } from '../types'

/** A persisted alias plus its normalized doc-id key and server timestamps. */
export type PayerAliasRecord = PayerAlias & {
  key: string
  createdAt?: string
  updatedAt?: string
}

/**
 * Read all payer aliases for a clinic. Returns [] on permission/network
 * failure — callers must not block on this.
 */
export async function fetchPayerAliases(
  orgId: string,
  clinicId: string,
): Promise<PayerAliasRecord[]> {
  if (!orgId || !clinicId) return []
  try {
    const snap = await getDocs(
      collection(db, 'organizations', orgId, 'clinics', clinicId, 'payerAliases'),
    )
    return snap.docs.map((d) => ({
      key: d.id,
      ...(d.data() as PayerAlias & { createdAt?: string; updatedAt?: string }),
    }))
  } catch {
    return []
  }
}

export interface SetPayerAliasInput {
  clinicId: string
  /** Raw shorthand as the user typed/saw it; normalized to the key server-side. */
  shorthand: string
  route: PayerAliasRoute
  tradingPartnerServiceId: string
  stediPayerId?: string | null
  resolvedName?: string
}

/**
 * Create or confirm a per-clinic alias via the backend callable. Resolves to
 * the normalized key the server stored it under.
 */
export async function setPayerAlias(
  input: SetPayerAliasInput,
): Promise<{ key: string }> {
  const fn = httpsCallable<SetPayerAliasInput, { key: string }>(
    functions,
    'setPayerAlias',
  )
  const res = await fn(input)
  return res.data
}

// ── The staff console's Payer Mappings board ────────────────────────────
// Six callables behind the staff gate (functions/src/staff/payerAliases.*).
// Every write goes through eligibility's published review service; every
// action carries the row's `expected` so it applies only to the rule as the
// board showed it — a `failed-precondition` refusal means the rule changed
// since the list was loaded, and the board reloads.

/** One rule on the board, as `listPayerAliasesForStaff` returns it. */
export interface PayerAliasBoardRow {
  orgId: string
  orgName: string
  clinicId: string
  clinicName: string
  key: string
  /** The insurance name exactly as the practice's system sent it. */
  raw: string
  route: PayerAliasRoute
  tradingPartnerServiceId: string
  stediPayerId: string | null
  resolvedName: string
  source: PayerAliasSource
  /** The review state of a self-written rule; null for a hand-set one. */
  reviewState: PayerAliasReviewState | null
  learned: PayerAliasLearned | null
  decidedBy: PayerAliasDecidedBy | null
  health: PayerAliasHealth | null
  createdAt: string | null
  updatedAt: string | null
}

/** The rule as the board showed it (see `PayerAliasExpected`). */
export function expectedOf(row: PayerAliasBoardRow): PayerAliasExpected {
  return {
    exists: true,
    source: row.source,
    review:
      row.reviewState === 'approved' || row.reviewState === 'rejected'
        ? row.reviewState
        : null,
    route: row.route,
    tradingPartnerServiceId: row.tradingPartnerServiceId,
  }
}

/** The board's one load: every rule at an active clinic of every practice. */
export async function listPayerAliasesForStaff(): Promise<PayerAliasBoardRow[]> {
  const fn = httpsCallable<Record<string, never>, { rules: PayerAliasBoardRow[] }>(
    functions,
    'listPayerAliasesForStaff',
  )
  const res = await fn({})
  return res.data.rules
}

export interface ReviewPayerAliasInput {
  orgId: string
  clinicId: string
  key: string
  decision: PayerAliasReview
  expected: PayerAliasExpected
}

/** Approve or reject a self-written rule. */
export async function reviewPayerAliasForStaff(
  input: ReviewPayerAliasInput,
): Promise<void> {
  const fn = httpsCallable<ReviewPayerAliasInput, { ok: true }>(
    functions,
    'reviewPayerAliasForStaff',
  )
  await fn(input)
}

export interface SetPayerAliasForStaffInput extends SetPayerAliasInput {
  orgId: string
  expected: PayerAliasExpected
}

/** Add a rule for any active clinic, or change an existing rule's insurer. */
export async function setPayerAliasForStaff(
  input: SetPayerAliasForStaffInput,
): Promise<{ key: string }> {
  const fn = httpsCallable<SetPayerAliasForStaffInput, { key: string }>(
    functions,
    'setPayerAliasForStaff',
  )
  const res = await fn(input)
  return res.data
}

export interface DeletePayerAliasForStaffInput {
  orgId: string
  clinicId: string
  key: string
  expected: PayerAliasExpected
}

/** Delete a rule — audited in both logs, in the same batch as the delete. */
export async function deletePayerAliasForStaff(
  input: DeletePayerAliasForStaffInput,
): Promise<void> {
  const fn = httpsCallable<DeletePayerAliasForStaffInput, { ok: true }>(
    functions,
    'deletePayerAliasForStaff',
  )
  await fn(input)
}

/** The check behind a rule, read-only: the stored result, the subscriber as
 *  sent, the insurer asked and the provider — never the raw 271. */
export interface PayerAliasCheckView {
  checkId: string
  /** `recorded`: the writer stamped this check on the rule. `found`: the rule
   *  predates that record and this is the check found to have preceded it —
   *  same name, clinic and insurer, within the hour before the rule was written. */
  origin: 'recorded' | 'found'
  clinicId: string | null
  status: string | null
  outcome: string | null
  checkedAt: string | null
  result: EligibilityResultWithMembers | null
  subscriber: {
    firstName: string
    lastName: string
    memberId: string
    dateOfBirth: string
  } | null
  payer: {
    name: string | null
    requestPayerName: string | null
    tradingPartnerServiceId: string | null
  }
  provider: { name: string; npi: string | null } | null
  /** For the check slideout, verbatim from the doc: both collection keys
   *  (the board runs `normalizeStoredCheck` over them, exactly as the Cockpit
   *  and Recent Checks do on read), the staff correction and payment pick,
   *  the chain keys and insurer id, and the clinic's timezone for the date
   *  stamp. */
  collectionResult: EligibilityCheckDoc['collectionResult'] | null
  collectionResultV2: EligibilityCheckDoc['collectionResultV2'] | null
  correction: CorrectionOverride | null
  userPick: UserPickOverride | null
  flowId: string | null
  eligibilityCheckSetId: string | null
  stediPayerId: string | null
  timezone: string | null
}

/** Open the check behind a rule. Throws `not-found` (with `details.reason`
 *  `no_check` / `missing`) when it cannot be shown; the view is recorded in
 *  both audit logs before anything is returned. */
export async function viewPayerAliasCheckForStaff(input: {
  orgId: string
  clinicId: string
  key: string
}): Promise<PayerAliasCheckView> {
  const fn = httpsCallable<typeof input, { check: PayerAliasCheckView }>(
    functions,
    'viewPayerAliasCheckForStaff',
  )
  const res = await fn(input)
  return res.data.check
}

/** A directory hit with the clinic's successful-check count beside it. */
export interface StaffPayerSearchHit {
  name: string
  tradingPartnerServiceId: string
  stediId: string | null
  logoUrl: string | null
  usageCount?: number
}

/** The payer directory search with a clinic's usage counts — what the board's
 *  pickers hand to `PayerSearch` as their `searchFn`. */
export async function searchPayersForStaff(input: {
  orgId: string
  clinicId: string
  query: string
}): Promise<{ payers: StaffPayerSearchHit[] }> {
  const fn = httpsCallable<typeof input, { payers: StaffPayerSearchHit[] }>(
    functions,
    'searchPayersForStaff',
  )
  const res = await fn(input)
  return res.data
}
